import React from 'react';
import { MODE_NAMES, arpeggioTypesFor } from '../../lib/theory';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import {
  Tray, ControlCard, TrayLabel, KeyRows, StringsCard, StandardQuickButtons,
} from '../shared';

// ── Data ──────────────────────────────────────────────────────────────────────
// Only display labels live here. The option VALUES are the state values App.js and the
// SEO deep links use ('Full Scale', 'Pentatonic', 'Modes', 'Arpeggios').

/** Structure labels: board 18 (phone) uses the short ones, board 20 (desktop) spells them out. */
const STRUCTURE_OPTIONS_PHONE = [
  { value: 'Full Scale', label: 'Scale' },
  { value: 'Pentatonic', label: 'Penta' },
  { value: 'Modes', label: 'Modes' },
  { value: 'Arpeggios', label: 'Arps' },
];
const STRUCTURE_OPTIONS_DESKTOP = [
  { value: 'Full Scale', label: 'Scale' },
  { value: 'Pentatonic', label: 'Pentatonic' },
  { value: 'Modes', label: 'Modes' },
  { value: 'Arpeggios', label: 'Arpeggios' },
];

/** Strings low to high, matching mandolinStringFocus. */
const STRINGS = [
  { label: 'G', ariaLabel: 'G string (lowest)' },
  { label: 'D', ariaLabel: 'D string' },
  { label: 'A', ariaLabel: 'A string' },
  { label: 'E', ariaLabel: 'E string (highest)' },
];

// The mandolin System row (Standard / FFcP), the Shape row (FFcP fingers), Chop Chords and
// Double Stops are hidden for now (rebuild plan, open questions 1 and 7). The theory code and the
// fretboard support stay in place; App.js pins the system to 'Standard' and the shape to
// 'All Shapes' so the fretboard always draws the standard view.

// ── Controls: Key card + Strings card ─────────────────────────────────────────

/**
 * Mandolin controls (board 18 phone / board 20 desktop): the Key and Strings cards.
 * The quick buttons live in {@link MandolinQuickButtons}; the page layout puts them under the cards.
 */
export default function MandolinControls({
  mandolinKey,         onMandolinKeyChange,
  mandolinTonality,    onMandolinTonalityChange,
  mandolinStructure,   onMandolinStructureChange,
  mandolinMode,        onMandolinModeChange,
  mandolinStringFocus, onMandolinStringToggle,
  mandolinArpeggioType, onMandolinArpeggioTypeChange,
}) {
  const isDesktop = useIsDesktop();
  const size = isDesktop ? 'md' : 'sm';
  const isModes = mandolinStructure === 'Modes';
  const isArpeggios = mandolinStructure === 'Arpeggios';
  const { basic, extensions } = arpeggioTypesFor(mandolinTonality);

  return (
    <>
      <ControlCard title="Key" size={size}>
        <KeyRows
          isDesktop={isDesktop}
          keyValue={mandolinKey}
          onKeyChange={onMandolinKeyChange}
          tonality={mandolinTonality}
          onTonalityChange={onMandolinTonalityChange}
          // A mode sets its own quality, so Major/Minor sits out while Modes is on.
          tonalityDisabled={isModes}
          structureOptions={STRUCTURE_OPTIONS_PHONE}
          structureOptionsDesktop={STRUCTURE_OPTIONS_DESKTOP}
          structure={mandolinStructure}
          onStructureChange={onMandolinStructureChange}
        />

        {isModes && (
          <>
            {isDesktop && <TrayLabel className="mt-1">Mode</TrayLabel>}
            <Tray
              ariaLabel="Mode"
              options={MODE_NAMES}
              value={mandolinMode}
              onChange={onMandolinModeChange}
              columns={isDesktop ? 3 : 4}
            />
          </>
        )}

        {isArpeggios && (
          <>
            {isDesktop && <TrayLabel className="mt-1">Basic</TrayLabel>}
            <Tray
              ariaLabel="Basic arpeggio type"
              options={basic}
              value={mandolinArpeggioType}
              onChange={onMandolinArpeggioTypeChange}
              columns={5}
            />
            {isDesktop && <TrayLabel>Extended</TrayLabel>}
            <Tray
              ariaLabel="Extended arpeggio type"
              options={extensions}
              value={mandolinArpeggioType}
              onChange={onMandolinArpeggioTypeChange}
              {...(isDesktop ? { columns: 5 } : { scroll: true })}
            />
          </>
        )}
      </ControlCard>

      <StringsCard strings={STRINGS} focus={mandolinStringFocus} onToggle={onMandolinStringToggle} size={size} />
    </>
  );
}

// ── Quick buttons: Filter / Labels / Reset ────────────────────────────────────

/**
 * The three quick buttons for the layout's `quickButtons` slot.
 * Filter cycles All Notes -> Triads Only -> Roots Only and is greyed for Arpeggios (every chord tone
 * always shows). Labels toggles Note Names <-> Intervals ('None' counts as Names).
 */
export function MandolinQuickButtons({
  mandolinStructure,
  mandolinFilter, onMandolinFilterChange,
  mandolinLabel,  onMandolinLabelChange,
  onMandolinReset,
}) {
  const isDesktop = useIsDesktop();
  return (
    <StandardQuickButtons
      size={isDesktop ? 'md' : 'sm'}
      filter={mandolinFilter}
      onFilterChange={onMandolinFilterChange}
      filterDisabled={mandolinStructure === 'Arpeggios'}
      label={mandolinLabel}
      onLabelChange={onMandolinLabelChange}
      onReset={onMandolinReset}
    />
  );
}
